module Main (main) where

import Prelude hiding (div)

import Bound (closed)
import Bounded (BoundsCheckerQuery(..), Lambda, boundsChecker)
import CEK as CEK
import Control.Comonad (extract)
import Control.Monad.State.Class (put)
import Data.Identity (Identity)
import Data.Maybe (Maybe(..))
import Effect (Effect)
import Effect.Class (class MonadEffect)
import Effect.Class.Console (log)
import Eval (StepperQuery(..), stepper)
import Expr (Expr, ParserQuery(Parse), parserComponent)
import Expr as Expr
import Halogen
  ( Component
  , ComponentHTML
  , HalogenM
  , HalogenQ
  , Slot
  , liftEffect
  , mkComponent
  , mkEval
  , query
  , raise
  )
import Halogen.Aff (runHalogenAff, awaitBody)
import Halogen.HTML
  ( HTML
  , PropName(PropName)
  , a
  , button
  , div
  , div_
  , h2_
  , h3_
  , p_
  , slot
  , slot_
  , text
  , textarea
  )
import Halogen.HTML.Events (onClick, onValueInput)
import Halogen.HTML.Properties (href, id, prop)
import Halogen.VDom.Driver (runUI)
import Test.QuickCheck.Gen (randomSampleOne)
import Token (TokenizerQuery(..), tokenizer)
import Type.Proxy (Proxy(..))

inputTA :: forall w. String -> HTML w String
inputTA val = textarea
  [ prop (PropName "value") val, onValueInput identity ]

aslTokenizer :: Proxy "tokenizer"
aslTokenizer = Proxy

aslRandomExpr :: Proxy "randomExpr"
aslRandomExpr = Proxy

aslParser :: Proxy "parser"
aslParser = Proxy

aslCekMachine :: Proxy "cekMachine"
aslCekMachine = Proxy

aslBoundsChecker :: Proxy "boundsChecker"
aslBoundsChecker = Proxy

aslStepper :: Proxy "stepper"
aslStepper = Proxy

type AppComponents =
  ( tokenizer :: Slot TokenizerQuery Void Unit
  , randomExpr :: Slot Identity Expr Unit
  , parser :: Slot ParserQuery Void Unit
  , cekMachine :: Slot CEK.Query Void Unit
  , boundsChecker :: Slot BoundsCheckerQuery Void Unit
  , stepper :: Slot StepperQuery (Lambda Void) Unit
  )

newtype StandardTerm = MkStandardTerm { name :: String, value :: String }

buttonStandardTerm :: forall w. StandardTerm -> HTML w String
buttonStandardTerm (MkStandardTerm term) = button [ onClick (const term.value) ]
  [ text term.name ]

standardTerms :: Array StandardTerm
standardTerms = map MkStandardTerm
  [ { name: "I", value: "\\x.x" }
  , { name: "S", value: "\\x.\\y.\\z.x z (y z)" }
  , { name: "K", value: "\\x.\\y.x" }
  , { name: "B", value: "\\x.\\y.\\z.x (y z)" }
  , { name: "C", value: "\\x.\\y.\\z.x z y" }
  , { name: "W", value: "\\x.\\y.x y y" }
  , { name: "omega", value: "\\x.x x" }
  , { name: "Omega", value: "(\\x.x x)\\x.x x" }
  , { name: "T", value: "\\x.\\y.x" }
  , { name: "F", value: "\\x.\\y.y" }
  ]

randomExprComponent ::
  forall input m. MonadEffect m => Component Identity input Expr m
randomExprComponent = mkComponent
  { initialState: const unit
  , render
  , eval: mkEval
      { initialize: Nothing
      , receive: const Nothing
      , handleAction
      , handleQuery: pure <<< Just <<< extract
      , finalize: Nothing
      }
  }
  where
  render _unit = button [ onClick (const unit) ] [ text "Random Expression" ]
  handleAction _unit = do
    expr <- liftEffect (randomSampleOne Expr.generator)
    raise expr

renderApp ::
  forall m. MonadEffect m => String -> ComponentHTML String AppComponents m
renderApp val =
  div
    [ id "app" ]
    [ h2_ [ text "Input Program / Source Text" ]
    , p_ [ text "Enter lambda expression directly" ]
    , div_ [ inputTA val ]
    , p_
        [ text "Or, select from some "
        , a [ href "https://en.wikipedia.org/wiki/Lambda_calculus#Standard_terms" ]
            [ text "standard terms" ]
        , text "."
        ]
    , div_ (map buttonStandardTerm standardTerms)
    , div_ [ slot aslRandomExpr unit randomExprComponent unit Expr.print ]
    , h2_ [ text "Static Analysis / Compilation" ]
    , h3_ [ text "Tokenization" ]
    , p_
        [ text
            "Fundamentally, tokenization takes an external input stream (usually bytes / octets)"
        , text
            " and converts to a stream with elements (\"tokens\") suitable for input to the parser."
        , text "  Tokenization is reset anytime the input changes."
        ]
    , slot_ aslTokenizer unit tokenizer unit
    , h3_ [ text "Parsing" ]
    , p_
        [ text
            "Parsing takes a stream and discovers / applies a tree struture to generate an abstract syntax tree (AST)."
        , text "  Parsing is reset on successful tokenization."
        ]
    , slot_ aslParser unit parserComponent unit
    , h3_ [ text "CEK Machine evaluation" ]
    , slot_ aslCekMachine unit CEK.component unit
    , h3_ [ text "Scope Checking / Nameless Represenation" ]
    , p_
        [ text
            "In this implementation we convert references to a nameless representation using"
        , text
            " (generalized) DeBruijn indexes.  Out-of-scope varible references are highlighted."
        , text "  If the are any, scope checking has failed."
        ]
    , slot_ aslBoundsChecker unit boundsChecker unit
    , h2_ [ text "Evaluation" ]
    , p_
        [ text "Evaluation is reset when the static analysis successfully changes."
        , text
            "  The next small step in evaluation is named, clicking that step name will advance."
        ]
    , slot_ aslStepper unit stepper unit
    ]

evalApp ::
  forall query input output m.
  (HalogenQ query String input) ~>
    (HalogenM String String AppComponents output m)
evalApp =
  mkEval
    { finalize: Nothing
    , receive: const Nothing
    , handleAction
    , handleQuery: const (pure Nothing)
    , initialize: Nothing
    }
  where
  handleAction str = do
    mToks <- map join $ query aslTokenizer unit
      (Tokenize str Just (const Nothing))
    case mToks of
      Nothing -> pure unit
      Just toks -> do
        mExpr <- query aslParser unit (Parse toks identity)
        case mExpr of
          Nothing -> pure unit
          Just expr -> do
            _ <- query aslCekMachine unit (CEK.Init expr)
            bexpr <- map join $ query aslBoundsChecker unit (BoundsCheck expr closed)
            case bexpr of
              Nothing -> pure unit
              Just init -> void $ query aslStepper unit (NewStart init)
    put str

app ::
  forall query input output m. MonadEffect m => Component query input output m
app =
  mkComponent
    { initialState: const ""
    , render: renderApp
    , eval: evalApp
    }

main :: Effect Unit
main = runHalogenAff do
  body <- awaitBody
  log "Ready to runUI"
  runUI app unit body
